import { useRef, useState } from 'react'
import ReactTooltip from 'react-tooltip'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import Info from '@/assets/common/info.inline.svg'
import PrimaryCard from '@/components/Common/PrimaryCard'
import ArrowDown from '@/assets/common/arrowDown.inline.svg'
import { StencilCDNImage } from '@/components/Common/StencilImage'

export type DataType = Array<{
  title?: string
  value?: string | number
  description?: string
  title_logo?: string
  valueClassNames?: string
  navlink?: string
}>

export function KeyValueTableExpandable({
  data = [],
  sectionHeading,
  description,
  minToShow = 5,
  expanded_label = 'See less',
  collapsed_label = 'See more',
  containerClass,
  col1heading,
  col2heading,
  childClass = '',
  headingClassName = '',
}: {
  data: DataType
  sectionHeading: string
  description?: String
  minToShow?: number
  expanded_label?: string
  collapsed_label?: string
  containerClass?: string
  col1heading?: string
  col2heading?: string
  childClass?: string
  headingClassName?: string
}) {
  const [showAll, setShowAll] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  if (!(data.length > 0)) {
    return null
  }

  return (
    <PrimaryCard
      containerClass={`mt-6 ${containerClass}`}
      childClass={childClass}
    >
      <div className="flex items-center justify-between" ref={ref}>
        <h2 className={classNames('text-xl font-medium', headingClassName)}>
          {sectionHeading}
        </h2>
        {description && (
          <div
            className="ml-2"
            data-tip={description}
            data-for="InstitutionalHoldings"
            data-place="bottom"
          >
            <span className="cursor-pointer">
              <Info className="h-4 w-4" />
            </span>
            <ReactTooltip
              className="w-48 bg-black text-xs font-thin text-white"
              id="valuation-section-highlight"
            />
          </div>
        )}
      </div>

      <div>
        <KeyValueTableComponent
          data={showAll ? data : data.slice(0, minToShow)}
          col1heading={col1heading}
          col2heading={col2heading}
        />
      </div>
      {data.length > minToShow && (
        <div
          className="text-brand-blue-azureRadiance flex w-full items-center justify-center 
 "
        >
          {showAll ? (
            <button
              className="mt-4 flex items-center justify-center text-sm "
              onClick={() => {
                setShowAll(false)
                ref.current?.scrollIntoView()
              }}
            >
              {expanded_label}
              <div className="h-6 w-6 rotate-180">
                <ArrowDown />
              </div>
            </button>
          ) : (
            <button
              className="mt-4 flex items-center justify-center text-sm "
              onClick={() => setShowAll(true)}
            >
              {collapsed_label}
              <div className="h-6 w-6 ">
                <ArrowDown />
              </div>
            </button>
          )}
        </div>
      )}
    </PrimaryCard>
  )
}

export function KeyValueTableComponent({
  data = [],
  col1heading,
  col2heading,
}: {
  data: DataType
  col1heading?: string
  col2heading?: string
}) {
  const router = useRouter()
  return (
    <table className="mt-4 w-full">
      {col1heading && col2heading && (
        <thead>
          <tr className="text-brand-grey-base flex w-full items-center justify-between px-4 text-sm">
            <td>{col1heading}</td> <td>{col2heading}</td>
          </tr>
        </thead>
      )}
      <tbody>
        {data.map(
          (
            { title, value, description, title_logo, valueClassNames, navlink },
            index,
          ) => {
            if (
              !(
                title &&
                (typeof value === 'number' || typeof value === 'string')
              )
            ) {
              return null
            }

            const isLast = data.length - 1 === index

            return (
              <tr
                key={`${title}-${description}`}
                className={classNames(
                  'group flex items-center justify-between py-4 text-sm',
                  {
                    'border-b-1': !isLast,
                    'hover:cursor-pointer': navlink,
                  },
                )}
                onClick={() => {
                  if (navlink) {
                    void router.push(navlink)
                  }
                }}
              >
                <td className="w-6/10 flex items-center gap-2">
                  {title_logo && (
                    <StencilCDNImage
                      src={title_logo}
                      width={30}
                      height={30}
                      className="shadow-one rounded-full border-2"
                      alt={title}
                    />
                  )}
                  <span
                    className={classNames({
                      'group-hover:text-brand-blue-azureRadiance': navlink,
                    })}
                  >
                    {title}
                  </span>
                  {description && (
                    <div
                      className="ml-2"
                      data-tip={description}
                      data-for={`${title}-${description}`}
                      data-place="bottom"
                    >
                      <span className="cursor-pointer">
                        <Info className="h-4 w-4" />
                      </span>
                      <ReactTooltip
                        className="w-48 bg-black text-xs font-thin text-white"
                        id={`${title}-${description}`}
                      />
                    </div>
                  )}
                </td>
                <td className={valueClassNames}>{value}</td>
              </tr>
            )
          },
        )}
      </tbody>
    </table>
  )
}
